import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { filter, first, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as userActions from 'src/app/store/actions/user.action';
import * as userSelectors from 'src/app/store/selectors/user.selector';
import { MandatePermissions } from '../../models/enum/mandate-permissions.enum';
import { IMandateType, MandateTypes } from '../../models/user/mandate.model';

@Injectable({
  providedIn: 'root'
})
export class MandateService {

  private ssin$: Observable<string> = this.store.select(userSelectors.selectPatientSSIN);
  private user$ = this.store.select(userSelectors.selectUserStateUser);
  private mandatePermissions = MandatePermissions;
  private mandateType$ = this.store.select(userSelectors.selectMandateType);

  constructor(
    private http: HttpClient,
    private store: Store
  ) {
    this.getMandateType();
  }

  private getMandateType(): void {
    this.ssin$.pipe(
      filter(ssin => !!ssin),
      withLatestFrom(this.user$),
      switchMap(([ssin, user]) => {
          if (!user.mandator) {
            return of({mandateType: 'USER'});
          } else {
            return this.http.get('/v1/mandateType' + '?ssin=' + ssin);
          }
        }
      )).subscribe({
      next: (res: IMandateType) => {
        this.store.dispatch(
          userActions.setMandateType({mandateType: res})
        );
      },
      error: () => {
        this.store.dispatch(
          userActions.setMandateType({
            mandateType: {mandateType: 'FAILED'},
          })
        );
      }
    });
  }

  private getPermissionsForMandateType(mandateType: MandateTypes): MandatePermissions[] {
    switch (mandateType.toUpperCase()) {
      case 'USER':
        return [
          this.mandatePermissions.viewVisiFlag,
          this.mandatePermissions.updateVisiFlag,
          this.mandatePermissions.consultDiaryNotes,
          this.mandatePermissions.consultMedicationScheme,
          this.mandatePermissions.deletePrescription,
          this.mandatePermissions.reservePrescription,
          this.mandatePermissions.viewReservationFlag,
          this.mandatePermissions.consultPrescriptions,
          this.mandatePermissions.consultDeliveredMedications
        ];
      case 'GMH':
        return [
          this.mandatePermissions.viewVisiFlag,
          this.mandatePermissions.updateVisiFlag,
          this.mandatePermissions.consultDiaryNotes,
          this.mandatePermissions.consultMedicationScheme,
          this.mandatePermissions.deletePrescription,
          this.mandatePermissions.reservePrescription,
          this.mandatePermissions.viewReservationFlag,
          this.mandatePermissions.consultPrescriptions,
          this.mandatePermissions.consultDeliveredMedications
        ];
      case 'PMH':
        return [
          this.mandatePermissions.reservePrescription,
          this.mandatePermissions.viewReservationFlag,
          this.mandatePermissions.consultPrescriptions
        ];
      default:
        return [];
    }
  }

  hasAccess(requestedPermission: MandatePermissions): Observable<boolean> {
    return this.mandateType$
      .pipe(
        first(mT => mT.mandateType !== undefined),
        map(mandate => this.getPermissionsForMandateType(mandate.mandateType).includes(requestedPermission))
      );
  }
}
